// Lightweight Google reCaptcha v3 helper (invisible / score based).
//
// The script is loaded once, on demand, without adding an npm dependency or a
// top-level provider. Every function degrades gracefully: with no site key, no
// network, or a slow Google response it resolves to `null` instead of throwing,
// so the calling form is never blocked (нічого не ламається).

const SCRIPT_ID = 'recaptcha-v3-script';

let loadPromise = null;

// Injects the reCaptcha v3 script (`api.js?render=<siteKey>`) a single time and
// resolves with the global `grecaptcha` object once it is available.
export const loadRecaptchaV3 = (siteKey) => {
  if (
    !siteKey ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return Promise.resolve(null);
  }

  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    const finish = () => resolve(window.grecaptcha || null);

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (window.grecaptcha) return finish();
      existing.addEventListener('load', finish);
      existing.addEventListener('error', () => resolve(null));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = finish;
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

  return loadPromise;
};

// Runs a reCaptcha v3 assessment and returns the token (string) or `null`.
// A timeout guarantees the caller is never left hanging if Google is slow.
export const executeRecaptchaV3 = async (
  siteKey,
  action = 'submit',
  timeoutMs = 4000
) => {
  if (!siteKey) return null;

  try {
    const grecaptcha = await loadRecaptchaV3(siteKey);
    if (!grecaptcha || !grecaptcha.execute) return null;

    const run = new Promise((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
      });
    });

    const timeout = new Promise((resolve) =>
      setTimeout(() => resolve(null), timeoutMs)
    );

    return await Promise.race([run, timeout]);
  } catch (e) {
    return null;
  }
};
